import { useRef, useState, useEffect } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { getUploadUrl, uploadFile } from '../../services/api/fileService';
import LoadingRing from '../ui-components/LoadingRing';

interface ImageUploadLabels {
    dragDrop?: string;
    dropHere?: string;
    dropToReplace?: string;
    change?: string;
    remove?: string;
    formats?: string;
    imageAlt?: string;
}

interface ImageUploadProps {
    imageUrl?: string;
    onImageUploaded: (fileId: string | null) => void;
    className?: string;
    labels?: ImageUploadLabels;
    compact?: boolean;
}

const defaultLabels: ImageUploadLabels = {
    dragDrop: 'Drag & Drop or click to upload',
    dropHere: 'Drop image here',
    dropToReplace: 'Drop to replace',
    change: 'Change',
    remove: 'Remove image',
    imageAlt: 'Uploaded image',
};

export default function ImageUpload({
    imageUrl,
    onImageUploaded,
    className,
    labels = {},
    compact = false,
}: ImageUploadProps) {
    const mergedLabels = { ...defaultLabels, ...labels };
    const inputRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(imageUrl || null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);

    // Sync preview state when imageUrl prop changes
    useEffect(() => {
        if (imageUrl) {
            setPreview(imageUrl);
        }
    }, [imageUrl]);

    const handleClick = () => {
        inputRef.current?.click();
    };

    const processFile = async (file: File) => {
        if (!file.type.startsWith('image/')) return;

        // Show local preview immediately
        const objectUrl = URL.createObjectURL(file);
        setPreview(objectUrl);

        setIsUploading(true);
        try {
            const uploadInfo = await getUploadUrl({
                ownerType: 'department',
                filename: file.name,
                fileType: file.type,
            });

            const response = await uploadFile(uploadInfo.url, file);
            if (response === 'OK' && uploadInfo.file?._id) {
                onImageUploaded(uploadInfo.file._id);
            } else {
                // Upload failed, revert preview
                setPreview(imageUrl || null);
                onImageUploaded(null);
            }
        } catch (error) {
            console.error('Image upload failed:', error);
            setPreview(imageUrl || null);
            onImageUploaded(null);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await processFile(file);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current++;
        if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current--;
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounterRef.current = 0;

        const file = e.dataTransfer?.files?.[0];
        if (file) {
            await processFile(file);
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        setPreview(null);
        onImageUploaded(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div
            className={cn('relative flex-1', className)}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
            />

            {preview ? (
                <div className="relative group h-full">
                    <img
                        src={preview}
                        alt={mergedLabels.imageAlt}
                        onClick={compact ? handleClick : undefined}
                        className={cn(
                            'absolute inset-0 w-full h-full rounded-md object-cover border transition-colors',
                            compact && 'cursor-pointer',
                            isDragging ? 'border-foreground' : 'border-control'
                        )}
                    />
                    {compact ? (
                        <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute right-1 top-1 h-6 w-6"
                            aria-label={mergedLabels.remove}
                            onClick={handleRemove}
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    ) : (
                        <div
                            className={cn(
                                'absolute inset-0 transition-opacity rounded-md flex items-center justify-center gap-2',
                                isDragging
                                    ? 'bg-black/60 opacity-100'
                                    : 'bg-black/50 opacity-0 group-hover:opacity-100'
                            )}
                        >
                            {isDragging ? (
                                <span className="text-sm font-medium">
                                    {mergedLabels.dropToReplace}
                                </span>
                            ) : (
                                <>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleClick}
                                    >
                                        {mergedLabels.change}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={handleRemove}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                    {isUploading && (
                        <div className="absolute inset-0 bg-black/60 rounded-md flex items-center justify-center">
                            <LoadingRing size="small" />
                        </div>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={handleClick}
                    disabled={isUploading}
                    className={cn(
                        'w-full h-full rounded-md border border-dashed bg-input text-foreground transition-colors flex flex-col items-center justify-center gap-1.5',
                        !compact && 'min-h-[150px]',
                        isDragging
                            ? 'border-foreground bg-accent'
                            : 'border-control hover:border-foreground hover:bg-accent'
                    )}
                >
                    {isUploading ? (
                        <LoadingRing size="small" />
                    ) : (
                        <>
                            <ImagePlus
                                className={cn(
                                    'text-subtle-foreground',
                                    compact ? 'h-5 w-5' : 'h-7 w-7'
                                )}
                            />
                            <span
                                className={cn(
                                    'text-center font-medium',
                                    compact
                                        ? 'px-2 text-[11px] leading-tight'
                                        : 'text-sm'
                                )}
                            >
                                {isDragging
                                    ? mergedLabels.dropHere
                                    : mergedLabels.dragDrop}
                            </span>
                            {mergedLabels.formats && !isDragging && (
                                <span
                                    className={cn(
                                        'text-center font-medium text-subtle-foreground',
                                        compact
                                            ? 'px-2 text-[10px] leading-tight'
                                            : 'text-[11px]'
                                    )}
                                >
                                    {mergedLabels.formats}
                                </span>
                            )}
                        </>
                    )}
                </button>
            )}
        </div>
    );
}
